import { useState, type ClipboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errText } from '../lib/api';
import ImageUpload from '../components/ImageUpload';
import Markdown from '../components/Markdown';
import { SUBJECTS, LEVELS } from '../lib/meta';

// 发布题目：拍照/选图 → qwen-vl OCR（公式→$...$）→ 可编辑题干 + 实时预览 → 发布
export default function PublishPage() {
  const nav = useNavigate();
  const [image, setImage] = useState('');
  const [stem, setStem] = useState('');
  const [subject, setSubject] = useState('math');
  const [level, setLevel] = useState('senior');
  const [keepImage, setKeepImage] = useState(true);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [err, setErr] = useState('');

  const runOcr = async () => {
    if (!image) return;
    setOcrLoading(true);
    setErr('');
    try {
      const { data } = await api.post('/xyz/ocr', { image });
      setStem(data.data.text);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setOcrLoading(false);
    }
  };

  // 剪贴板粘贴截图 → 自动 OCR → 追加到题干（免弹窗；参照 tex-exam 粘贴识别）
  const ocrDataUrl = async (dataUrl: string) => {
    setOcrLoading(true);
    setErr('');
    try {
      const { data } = await api.post('/xyz/ocr', { image: dataUrl });
      const text = (data.data.text || '').trim();
      if (!text) { setErr('未识别到文字'); return; }
      setStem((prev) => (prev ? `${prev}\n${text}` : text));
    } catch (e) {
      setErr(errText(e));
    } finally {
      setOcrLoading(false);
    }
  };

  const handlePaste = async (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const cd = e.clipboardData;
    let file: File | null = null;
    const item = Array.from(cd.items).find((it) => it.type.startsWith('image/'));
    if (item) file = item.getAsFile();
    // Linux 剪贴板工具有时把图片只放在 files 而非 items（tex-exam 同款兜底）
    if (!file && cd.files?.length) file = Array.from(cd.files).find((f) => f.type.startsWith('image/')) || null;
    if (!file) return; // 纯文字粘贴不拦截
    e.preventDefault();
    if (ocrLoading) return;
    if (file.size > 5 * 1024 * 1024) { setErr('图片不能超过 5MB，请压缩后重试'); return; }
    const reader = new FileReader();
    reader.onload = () => ocrDataUrl(String(reader.result));
    reader.readAsDataURL(file);
  };

  const publish = async () => {
    if (stem.trim().length < 2) {
      setErr('题干不能为空（先上传图片识别，或直接输入）');
      return;
    }
    setPublishing(true);
    setErr('');
    try {
      const { data } = await api.post('/xyz/problems', {
        subject, level, stem: stem.trim(), stemImage: keepImage ? image : '',
      });
      nav(`/problems/${data.data.problem._id}`);
    } catch (e) {
      setErr(errText(e));
      setPublishing(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <h1 className="mb-4 text-lg font-bold text-gray-900">发布题目</h1>

      <div className="space-y-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700">1️⃣ 题目照片</label>
          <ImageUpload dataUrl={image} onLoaded={setImage} label="上传题目照片" />
          {image && (
            <button
              onClick={runOcr}
              disabled={ocrLoading}
              className="mt-3 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {ocrLoading ? '⏳ 识别中（约 10-30 秒）…' : '🔍 识别文字'}
            </button>
          )}
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700">2️⃣ 题干（可编辑，公式用 $...$）</label>
          <textarea
            value={stem}
            onChange={(e) => setStem(e.target.value)}
            onPaste={handlePaste}
            rows={8}
            placeholder="识别结果会填到这里，也可以直接输入。公式如 $x^2+1$ 或 $\\frac{1}{2}$"
            className="w-full rounded-lg border border-gray-300 p-3 font-mono text-sm focus:border-brand-500 focus:outline-none"
          />
          {stem && (
            <div className="mt-2 rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="mb-1 text-xs text-gray-400">预览</p>
              <Markdown text={stem} className="text-sm" />
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">3️⃣ 科目</label>
            <select value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {SUBJECTS.map((s) => <option key={s.code} value={s.code}>{s.icon} {s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">学段</label>
            <select value={level} onChange={(e) => setLevel(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {LEVELS.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </div>
          <label className="mt-6 flex items-center gap-1.5 text-sm text-gray-600">
            <input type="checkbox" checked={keepImage} onChange={(e) => setKeepImage(e.target.checked)} />
            保留原题照片（批改时供 AI 对照）
          </label>
        </div>

        {err && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{err}</p>}

        <div className="flex gap-3">
          <button
            onClick={publish}
            disabled={publishing || stem.trim().length < 2}
            className="rounded-lg bg-brand-600 px-6 py-2.5 font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {publishing ? '发布中…' : '🚀 发布（消耗 1 次额度）'}
          </button>
          <button onClick={() => nav('/problems')} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-600">
            取消
          </button>
        </div>
      </div>
    </div>
  );
}
